import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { CreditCard, ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";
import { PROFILE_SETTINGS } from "../../routes/routes";

const PaymentDetailsPage = styled.div`
  background: #ededed;
  min-height: 100%;
  width: 100%;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const PaymentDetailsContainer = styled.div`
  padding: 2rem;
  width: 100%;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #111827;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  margin-bottom: 1.5rem;
  transition: color 0.2s ease;

  &:hover {
    color: #374151;
    text-decoration: underline;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PaymentCard = styled.div`
  background: transparent;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
`;

const SelectedPlanInfo = styled.div`
  background: #f5f8fe;
  border: 1px solid #93c5fd;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PlanInfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PlanInfoName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
`;

const PlanInfoDetails = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const FieldLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const TextInput = styled.input`
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #111827;
  background: transparent;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PromoCodeSection = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const PromoCodeInput = styled(TextInput)`
  flex: 1;
`;

const ApplyButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
`;

const OrderSummary = styled.div`
  background: #f3f3f3;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const OrderSummaryTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
`;

const OrderSummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.875rem;

  &:last-child {
    border-bottom: none;
    font-weight: 600;
    font-size: 1rem;
    padding-top: 1rem;
    margin-top: 0.5rem;
    border-top: 2px solid #e5e7eb;
  }
`;

const OrderSummaryLabel = styled.span`
  color: #6b7280;
`;

const OrderSummaryValue = styled.span`
  color: #111827;
  font-weight: 500;
`;

const PaymentMethodSection = styled.div`
  margin-bottom: 1.5rem;
`;

const LinkedCardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const LinkedCardDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`;

const CardBrand = styled.div`
  font-weight: 600;
  color: #111827;
  font-size: 0.875rem;
`;

const CardLast4 = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`;

const LinkCardButton = styled.button`
  background: #22c55e;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #16a34a;
  }
`;

const CardInputWrapper = styled.div`
  position: relative;
`;

const CardIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  display: flex;
  align-items: center;
`;

const CardNumberInput = styled(TextInput)`
  padding-left: 2.75rem;
`;

const TrialInfo = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: #1e40af;
  line-height: 1.5;
`;

const StartTrialButton = styled.button`
  width: 100%;
  background: #3b82f6;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
`;

const plans = [
  {
    id: "prospect",
    name: "Prospect",
    price: "$9",
    period: "month",
    scans: "500 monthly AI scans",
    buttonText: "1-Week Free Trial",
    badge: null,
  },
  {
    id: "minors",
    name: "Minors",
    price: "$19",
    period: "month",
    scans: "1,100 monthly AI scans",
    buttonText: "Get Started",
    badge: null,
  },
  {
    id: "rookie",
    name: "Rookie",
    price: "$39",
    period: "month",
    scans: "2,500 monthly AI scans",
    buttonText: "Get Started",
    badge: null,
  },
  {
    id: "all-star",
    name: "All-Star",
    price: "$59",
    period: "month",
    scans: "5,000 monthly AI scans",
    buttonText: "Get Started",
    badge: "Most Popular",
  },
  {
    id: "mvp",
    name: "MVP",
    price: "$99",
    period: "month",
    scans: "10,000 monthly AI scans",
    buttonText: "Get Started",
    badge: null,
  },
  {
    id: "hall-of-fame",
    name: "Hall of Fame",
    price: "$199",
    period: "month",
    scans: "25,000 monthly AI scans",
    buttonText: "Get Started",
    badge: null,
  },
];

function PaymentDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const [promoCode, setPromoCode] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [hasLinkedCard, setHasLinkedCard] = useState(false);
  const [linkedCardLast4, setLinkedCardLast4] = useState("");
  const [linkedCardBrand, setLinkedCardBrand] = useState("");

  // Get selected plan from location state or default to prospect
  const selectedPlanId = location.state?.selectedPlan || "prospect";
  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || plans[0];

  const handleApplyPromoCode = () => {
    // TODO: Implement promo code validation
    toast.info("Promo code applied");
  };

  const handleLinkCard = () => {
    // TODO: Implement card linking
    setHasLinkedCard(true);
    setLinkedCardLast4("6831");
    setLinkedCardBrand("VISA");
    toast.success("Card linked successfully");
  };

  const handleStartTrial = async () => {
    setIsSaving(true);
    try {
      // TODO: Implement API call to start trial
      // const token = localStorage.getItem("access");
      // const response = await fetch(`${API_BASE_URL}/api/v1/subscriptions/start-trial/`, {
      //   method: "POST",
      //   headers: {
      //     Authorization: `Bearer ${token}`,
      //     "Content-Type": "application/json",
      //   },
      //   body: JSON.stringify({
      //     planId: selectedPlanId,
      //     cardNumber: cardNumber,
      //     promoCode: promoCode,
      //   }),
      // });
      // 
      // if (!response.ok) {
      //   throw new Error("Failed to start trial");
      // }

      // Simulated API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast.success("Trial started successfully");
      navigate(PROFILE_SETTINGS);
    } catch (error) {
      console.error("Start trial error:", error);
      toast.error("Failed to start trial");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PaymentDetailsPage>
    <PaymentDetailsContainer>
      <BackLink onClick={() => navigate(PROFILE_SETTINGS)}>
        <ArrowLeft />
        Back to select plan
      </BackLink>

      <PaymentCard>
        <SelectedPlanInfo>
          <PlanInfoText>
            <PlanInfoName>{selectedPlan.name}</PlanInfoName>
            <PlanInfoDetails>
              {selectedPlan.price}/{selectedPlan.period} • {selectedPlan.scans}
            </PlanInfoDetails>
          </PlanInfoText>
        </SelectedPlanInfo>

        <FormRow>
          <FormField>
            <FieldLabel>Promo/Referral Code</FieldLabel>
            <PromoCodeSection>
              <PromoCodeInput
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter promo code"
              />
              <ApplyButton onClick={handleApplyPromoCode}>
                Apply
              </ApplyButton>
            </PromoCodeSection>
          </FormField>
        </FormRow>

        <OrderSummary>
          <OrderSummaryTitle>Order Summary</OrderSummaryTitle>
          <OrderSummaryRow>
            <OrderSummaryLabel>{selectedPlan.name} Plan</OrderSummaryLabel>
            <OrderSummaryValue>{selectedPlan.price}/month</OrderSummaryValue>
          </OrderSummaryRow>
          <OrderSummaryRow>
            <OrderSummaryLabel>Sales Tax</OrderSummaryLabel>
            <OrderSummaryValue>$0.00</OrderSummaryValue>
          </OrderSummaryRow>
          <OrderSummaryRow>
            <OrderSummaryLabel>Free Trial Period</OrderSummaryLabel>
            <OrderSummaryValue>1 week (7 days)</OrderSummaryValue>
          </OrderSummaryRow>
          <OrderSummaryRow>
            <OrderSummaryLabel>First billing date</OrderSummaryLabel>
            <OrderSummaryValue>Feb 5, 2026</OrderSummaryValue>
          </OrderSummaryRow>
          <OrderSummaryRow>
            <OrderSummaryLabel>Total for today</OrderSummaryLabel>
            <OrderSummaryValue>$0.00</OrderSummaryValue>
          </OrderSummaryRow>
        </OrderSummary>

        <PaymentMethodSection>
          <FieldLabel>Payment Method</FieldLabel>
          {hasLinkedCard ? (
            <LinkedCardInfo>
              <LinkedCardDetails>
                <CardBrand>{linkedCardBrand}</CardBrand>
                <CardLast4>•••• {linkedCardLast4}</CardLast4>
              </LinkedCardDetails>
              <LinkCardButton onClick={handleLinkCard}>
                link
              </LinkCardButton>
            </LinkedCardInfo>
          ) : (
            <CardInputWrapper>
              <CardIcon>
                <CreditCard size={20} />
              </CardIcon>
              <CardNumberInput
                type="text"
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                placeholder="Card number"
                maxLength={19}
              />
            </CardInputWrapper>
          )}
        </PaymentMethodSection>

        <TrialInfo>
          Because you're on a 1-week free trial, your card will not be charged until the first billing date. You can cancel your subscription anytime.
        </TrialInfo>

        <StartTrialButton onClick={handleStartTrial} disabled={isSaving || !cardNumber.trim()}>
          {isSaving ? "Processing..." : "Start 1-Week Free Trial"}
        </StartTrialButton>
      </PaymentCard>
    </PaymentDetailsContainer>
    </PaymentDetailsPage>
  );
}

export default PaymentDetails;



